import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'i18next';
import CalendarPicker from './CalendarPicker';
import AppSelect, { AppSelectOption } from './AppSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { ColDef } from './DataTable';

export interface ActiveFilter {
  colKey: string;
  label: string;
  value: string;
  valueTo?: string;
  values?: string[];   // multiselect: array of selected values
}

export function applyColumnFilters<T = any>(rows: T[], filters: ActiveFilter[], columns: ColDef<T>[]): T[] {
  if (!filters.length) return rows;
  return rows.filter(row => filters.every(f => {
    const col = columns.find(c => c.key === f.colKey);
    if (!col) return true;
    const val = col.filterExtract ? col.filterExtract(row) : String((row as any)[f.colKey] ?? '');
    if (col.filterType === 'daterange') {
      if (!val) return !f.value && !f.valueTo;
      const rt = new Date(val).getTime();
      if (f.value && rt < new Date(f.value + 'T00:00:00').getTime()) return false;
      if (f.valueTo && rt > new Date(f.valueTo + 'T23:59:59').getTime()) return false;
      return true;
    }
    if (col.filterType === 'select' || col.filterType === 'boolean') {
      if (f.values?.length) return f.values.includes(val);
      return val === f.value;
    }
    return val.toLowerCase().includes(f.value.toLowerCase());
  }));
}

export function applySearchAndFilters<T = any>(
  rows: T[],
  search: string,
  filters: ActiveFilter[],
  columns: ColDef<T>[],
  searchGet?: (row: T) => string,
): T[] {
  const filtered = applyColumnFilters(rows, filters, columns);
  const q = search.trim().toLowerCase();
  if (!q) return filtered;
  const textCols = columns.filter(c => c.filterType === 'text');
  const get = searchGet ?? ((row: T) =>
    textCols.map(c => (c.filterExtract ? c.filterExtract(row) : String((row as any)[c.key] ?? ''))).join(' '));
  return filtered.filter(row => get(row).toLowerCase().includes(q));
}

// ── SearchFilter ─────────────────────────────────────────────────────────────

interface SearchFilterProps<T = any> {
  columns?: ColDef<T>[];
  search: string;
  onSearch: (v: string) => void;
  filters?: ActiveFilter[];
  onFilters?: (f: ActiveFilter[]) => void;
  placeholder?: string;
  grow?: boolean;
}

type Draft = Record<string, { value: string; valueTo?: string; values?: string[] }>;

export default function SearchFilter<T = any>({
  columns = [], search, onSearch, filters = [], onFilters = () => {}, placeholder, grow,
}: SearchFilterProps<T>) {
  const filterableCols = useMemo(() => columns.filter(c => !!c.filterType), [columns]);

  const [dropOpen, setDropOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>({});
  const [badgeHover, setBadgeHover] = useState(false);
  const [dropCoords, setDropCoords] = useState<{ top: number; right: number }>({ top: -9999, right: -9999 });
  const anchorRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const a = anchorRef.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    setDropCoords({ top: r.bottom + 8, right: window.innerWidth - r.right });
  }, []);

  const openDrop = () => {
    const init: Draft = {};
    filters.forEach(f => { init[f.colKey] = { value: f.value, valueTo: f.valueTo, values: f.values }; });
    setDraft(init);
    setDropOpen(true);
  };

  const closeDrop = () => setDropOpen(false);

  const apply = useCallback(() => {
    const next: ActiveFilter[] = [];
    Object.entries(draft).forEach(([key, { value, valueTo, values }]) => {
      const col = columns.find(c => c.key === key);
      if (!col) return;
      if (col.filterType === 'daterange') {
        if (!value && !valueTo) return;
        const fmt = (d?: string) => d ? new Date(d + 'T00:00:00').toLocaleDateString() : '…';
        next.push({ colKey: key, label: `${col.label}: ${fmt(value)} — ${fmt(valueTo)}`, value: value ?? '', valueTo });
      } else if (col.multiSelect && col.filterType === 'select') {
        const vals = values ?? [];
        if (!vals.length) return;
        const labels = vals.map(v => col.filterOptions?.find(o => o.value === v)?.label ?? v);
        const label = labels.length <= 2
          ? `${col.label}: ${labels.join(', ')}`
          : `${col.label}: ${labels[0]}, ${labels[1]} (+${labels.length - 2})`;
        next.push({ colKey: key, label, value: vals[0], values: vals });
      } else {
        if (!value) return;
        const optLabel = col.filterOptions?.find(o => o.value === value)?.label;
        const label = (col.filterType === 'select' || col.filterType === 'boolean') && optLabel
          ? `${col.label}: ${optLabel}` : `${col.label}: ${value}`;
        next.push({ colKey: key, label, value });
      }
    });
    onFilters(next);
    setDropOpen(false);
  }, [draft, columns, onFilters]);

  const reset = useCallback(() => {
    setDraft({});
    onFilters([]);
    setDropOpen(false);
  }, [onFilters]);

  const removeFilter = (colKey: string) => onFilters(filters.filter(f => f.colKey !== colKey));

  const setField = (key: string, value: string) =>
    setDraft(d => ({ ...d, [key]: { ...(d[key] ?? {}), value } }));
  const setFieldTo = (key: string, valueTo: string) =>
    setDraft(d => ({ ...d, [key]: { ...(d[key] ?? {}), valueTo } }));
  const setMultiValues = (key: string, values: string[]) =>
    setDraft(d => ({ ...d, [key]: { ...(d[key] ?? {}), values, value: values[0] ?? '' } }));

  useLayoutEffect(() => {
    if (dropOpen) place();
  }, [dropOpen, place]);

  useEffect(() => {
    if (!dropOpen) return;
    const onScroll = () => place();
    const handle = (e: MouseEvent) => {
      const target = e.target as Element;
      if (target.closest('[data-appselect-portal]')) return;
      if (dropRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      setDropOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('mousedown', handle);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousedown', handle);
    };
  }, [dropOpen, place]);

  const rowBorder = '1px solid var(--border-input)';

  return (
    <div className={`sf-wrap${grow ? ' sf-grow' : ''}`}>

      {/* ── Search bar ── */}
      <div
        className="sf-bar"
        onMouseDown={e => { if (e.target === e.currentTarget) (e.currentTarget as HTMLElement).querySelector('input')?.focus(); }}
      >
        <span className="sf-lupa">🔍</span>
        <input
          className="sf-input"
          value={search}
          onChange={e => onSearch(e.target.value)}
          placeholder={placeholder ?? (t('SearchOrFilter') as string)}
        />

        {filterableCols.length > 0 && (
          <>
            <span className="sf-sep" />
            {filters.length > 0 && (
              <div
                style={{ position: 'relative', flexShrink: 0 }}
                onMouseEnter={() => setBadgeHover(true)}
                onMouseLeave={() => setBadgeHover(false)}
              >
                <span className="sf-filter-cnt">{filters.length}</span>
                {badgeHover && (
                  <div className="sf-badge-tip">
                    {filters.map(f => (
                      <div key={f.colKey} className="sf-badge-tip-row">
                        <span className="sf-badge-tip-lbl">{f.label}</span>
                        <button
                          type="button"
                          className="sf-badge-tip-x"
                          onMouseDown={e => { e.stopPropagation(); removeFilter(f.colKey); }}
                        >✕</button>
                      </div>
                    ))}
                    {filters.length > 1 && (
                      <div className="sf-badge-tip-foot">
                        <button
                          type="button"
                          className="sf-badge-tip-clear"
                          onMouseDown={e => { e.stopPropagation(); onFilters([]); }}
                        >{t('ClearAll') || 'Clear all'}</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div ref={anchorRef} style={{ flexShrink: 0 }}>
              <button
                type="button"
                className={`sf-filter-btn${dropOpen ? ' active' : ''}${filters.length > 0 ? ' sf-filter-on' : ''}`}
                onClick={dropOpen ? closeDrop : openDrop}
              >
                <FontAwesomeIcon icon={faFilter} style={{ fontSize: 10, opacity: 0.75 }} />
                <span className="sf-filter-label">{t('Filter') || 'Filter'}</span>
              </button>
            </div>

            {dropOpen && createPortal(
              <div
                ref={dropRef}
                data-portal-modal
                onMouseDown={e => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  top: dropCoords.top,
                  right: dropCoords.right,
                  zIndex: 'var(--z-portal)' as any,
                  background: 'var(--bg-form)',
                  border: rowBorder,
                  borderRadius: 10,
                  minWidth: 300,
                  boxShadow: '0 8px 28px rgba(0,0,0,.14)',
                  overflow: 'hidden',
                }}
              >
                {filterableCols.map((col, i) => {
                  const dv = draft[col.key]?.value ?? '';
                  const dvTo = draft[col.key]?.valueTo ?? '';
                  const dvVals = draft[col.key]?.values ?? [];
                  const isLast = i === filterableCols.length - 1;
                  const isMulti = col.multiSelect && col.filterType === 'select';
                  return (
                    <div
                      key={col.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '7px 12px',
                        borderBottom: isLast ? 'none' : rowBorder,
                      }}
                    >
                      <span style={{
                        flexShrink: 0,
                        width: 82,
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: 'var(--text-color-calendar-week)',
                      }}>
                        {col.label}
                      </span>

                      {col.filterType === 'daterange' ? (
                        <div style={{ display: 'flex', gap: 5, flex: 1, alignItems: 'center' }}>
                          <CalendarPicker
                            mode="single" variant="field" output="string"
                            value={dv} onChange={v => setField(col.key, v)}
                          />
                          <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>—</span>
                          <CalendarPicker
                            mode="single" variant="field" output="string"
                            value={dvTo} onChange={v => setFieldTo(col.key, v)}
                          />
                        </div>
                      ) : isMulti ? (
                        <AppSelect
                          size="sm"
                          multiple
                          values={dvVals}
                          onChangeMultiple={vals => setMultiValues(col.key, vals.map(String))}
                          options={(col.filterOptions ?? []) as AppSelectOption[]}
                          placeholder={t('Any') as string || 'Any'}
                          style={{ flex: 1 }}
                        />
                      ) : (col.filterType === 'select' || col.filterType === 'boolean') ? (
                        <AppSelect
                          size="sm"
                          value={dv || null}
                          onChange={v => setField(col.key, v == null ? '' : String(v))}
                          options={(col.filterOptions ?? []) as AppSelectOption[]}
                          placeholder={t('Any') || 'Any'}
                          style={{ flex: 1 }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={dv}
                          onChange={e => setField(col.key, e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') apply(); }}
                          placeholder={t('Any') || 'Any'}
                          style={{
                            flex: 1, height: 28, border: rowBorder, borderRadius: 6,
                            background: 'var(--bg-input)', color: 'var(--text-color)',
                            fontSize: 12, padding: '0 8px', outline: 'none', fontFamily: 'inherit',
                          }}
                        />
                      )}
                    </div>
                  );
                })}

                <div style={{
                  display: 'flex',
                  gap: 6,
                  padding: '8px 12px',
                  borderTop: rowBorder,
                  background: 'var(--bg-top)',
                }}>
                  <button
                    type="button"
                    onClick={reset}
                    style={{
                      height: 28, padding: '0 12px', borderRadius: 6,
                      border: rowBorder, background: 'transparent',
                      color: 'var(--text-color-calendar-week)', fontSize: 12,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {t('Reset') || 'Reset'}
                  </button>
                  <button
                    type="button"
                    onClick={apply}
                    style={{
                      flex: 1, height: 28, borderRadius: 6, border: 'none',
                      background: '#2563eb', color: '#fff',
                      fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {t('Apply') || 'Apply'}
                  </button>
                </div>
              </div>,
              document.body,
            )}
          </>
        )}
      </div>

    </div>
  );
}
